import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';

import { getBodyAgeGroup } from '@repo/ui';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import type {
  Gate,
  GateEventPropertiesMap,
  GateSharedProps,
} from 'contexts/analytics/events/gates';
import type { EventPropertiesMap } from 'contexts/analytics/types';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useFeature from 'services/features/useFeature';
import { getImpersonateId, getRoles } from 'store/user';
import {
  getGateEntrySurface,
  getGateExperience,
  getGateViewerRole,
} from '../utils/gateAnalytics';

type GateEventName<G extends Gate> = {
  [K in keyof GateEventPropertiesMap]: GateEventPropertiesMap[K]['gate'] extends G
    ? K
    : never;
}[keyof GateEventPropertiesMap];

type GateEventOwnProps<T extends keyof GateEventPropertiesMap> = Omit<
  GateEventPropertiesMap[T],
  keyof GateSharedProps | 'gate'
>;

export default function useGateAnalytics<G extends Gate>(gate: G) {
  const { trackEvent } = useAnalytics();
  const { currentKit } = useCurrentKitAndTinyAccount();
  const { isFeatureEnabled: gutBodyConnectionsEnabled } =
    useFeature('gut_x_axis');
  const impersonateId = useSelector(getImpersonateId);
  const roles = useSelector(getRoles);
  const location = useLocation();
  const isConditionDetail = !!useMatch(
    '/results/:kitId/conditions/metrics/:condition',
  );

  return useCallback(
    <T extends GateEventName<G>>(
      eventName: T,
      properties: GateEventOwnProps<T>,
    ) => {
      const kitType = currentKit?.sampling_type;
      if (!currentKit || !kitType) return;

      const sharedProps: GateSharedProps = {
        experience: getGateExperience({
          gutBodyConnectionsEnabled,
          isConditionDetail,
          kitType,
        }),
        entry_surface: getGateEntrySurface({
          gate,
          isConditionDetail,
          isInitialEntry: location.key === 'default',
          fromHomeCard:
            (location.state as { source?: string } | null)?.source ===
            'home_card',
        }),
        kit_type: kitType,
        body_variant: getBodyAgeGroup({
          age: currentKit.age_at_sampling ?? 0,
          pregnant: !!currentKit.pregnant,
        }),
        viewer_role: getGateViewerRole(impersonateId, roles),
      };

      trackEvent(eventName, {
        ...sharedProps,
        gate,
        ...properties,
      } as EventPropertiesMap[T]);
    },
    [
      currentKit,
      gate,
      gutBodyConnectionsEnabled,
      impersonateId,
      isConditionDetail,
      location.key,
      location.state,
      roles,
      trackEvent,
    ],
  );
}
